import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import { getAuditLogs } from "Api/apiService";

const METHOD_OPTIONS = ["ALL", "POST", "PUT", "PATCH", "DELETE"];
const ROLE_OPTIONS = ["all", "admin", "employee"];
const ACTION_OPTIONS = ["all", "create", "update", "status_update", "delete", "change"];

const formatDate = value => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return "-";
  return parsed.toLocaleString("en-US");
};

export default function AuditLogs() {
  const toast = useToast();
  const titleColor = useColorModeValue("navy.700", "white");
  const textColor = useColorModeValue("secondaryGray.700", "secondaryGray.300");

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [method, setMethod] = useState("ALL");
  const [role, setRole] = useState("all");
  const [action, setAction] = useState("all");

  const loadLogs = async () => {
    setLoading(true);
    try {
      const response = await getAuditLogs({
        q: q.trim() || undefined,
        method: method !== "ALL" ? method : undefined,
        role: role !== "all" ? role : undefined,
        action: action !== "all" ? action : undefined,
        limit: 100,
      });
      setLogs(response.data?.logs || []);
    } catch (error) {
      toast({
        title: "Failed to load audit logs",
        description: error?.response?.data?.message || error.message,
        status: "error",
        duration: 3200,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box pt="80px">
      <SimpleGrid columns={1} spacing="20px">
        <Card p="20px">
          <Flex justify="space-between" align="center" wrap="wrap" gap="10px" mb="12px">
            <Box>
              <Text fontSize="xl" fontWeight="700" color={titleColor}>
                Audit Logs
              </Text>
              <Text fontSize="sm" color={textColor}>
                Track who changed what in the admin portal.
              </Text>
            </Box>
            <Button colorScheme="blue" onClick={loadLogs} isLoading={loading}>
              Refresh
            </Button>
          </Flex>

          <HStack spacing="8px" wrap="wrap" mb="14px">
            <Input
              placeholder="Search email, route, or target id"
              value={q}
              onChange={e => setQ(e.target.value)}
              maxW="340px"
            />
            <Select value={method} onChange={e => setMethod(e.target.value)} w="130px">
              {METHOD_OPTIONS.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
            <Select value={role} onChange={e => setRole(e.target.value)} w="130px">
              {ROLE_OPTIONS.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
            <Select value={action} onChange={e => setAction(e.target.value)} w="160px">
              {ACTION_OPTIONS.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
            <Button onClick={loadLogs} isLoading={loading}>
              Apply
            </Button>
          </HStack>

          {!logs.length ? (
            <Text color={textColor}>No logs found.</Text>
          ) : (
            <Stack spacing="10px">
              {logs.map(log => (
                <Box
                  key={log._id}
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="12px"
                  p="12px"
                >
                  <Flex justify="space-between" align="center" wrap="wrap" gap="8px">
                    <Text fontWeight="800" color={titleColor}>
                      {log.actor?.email || "Unknown user"}
                    </Text>
                    <HStack spacing="8px">
                      <Badge colorScheme="purple">{log.method}</Badge>
                      <Badge colorScheme="blue">{log.action || "change"}</Badge>
                      <Badge colorScheme="gray">{log.actor?.role || "unknown"}</Badge>
                    </HStack>
                  </Flex>

                  <Text fontSize="sm" color={textColor}>
                    {formatDate(log.createdAt)} • Status {log.statusCode}
                  </Text>
                  <Text fontSize="sm" color={textColor}>
                    Path: {log.path}
                  </Text>
                  <Text fontSize="sm" color={textColor}>
                    Target: {log.targetId || "-"}
                  </Text>

                  {log.metadata?.requestBody ? (
                    <Box mt="8px" p="8px" bg="gray.50" borderRadius="8px">
                      <Text fontSize="xs" color={textColor}>
                        {JSON.stringify(log.metadata.requestBody)}
                      </Text>
                    </Box>
                  ) : null}
                </Box>
              ))}
            </Stack>
          )}
        </Card>
      </SimpleGrid>
    </Box>
  );
}
